import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { get, post, put } from "../api";
import { Time } from "../Time";
import type {
  CustomerDetail,
  PlatformDomainDirectory,
  PlatformDomainRow,
  PlatformDomainSync,
  RefreshResult,
} from "../api";

// The platform-domain directory: every tenant of the voice platform, and
// which customer each one belongs to.
//
// Assignment lives here rather than on the customer, because the relation
// runs that way: a tenant belongs to at most one customer, so "who owns
// this?" is a property of the domain and a single dropdown per row says it.
// Putting a multi-select on the customer instead would need a widget wide
// enough for L&T's eight tenants and would give two screens the chance to
// disagree about the same row.
//
// Nothing here renders ingested content as markup -- these strings come
// from another system of ours rather than from a customer, but the rule is
// the rule (invariant 21), so they are text nodes and nothing else.

type Filter = "all" | "unclaimed" | "claimed" | "issues";

export default function PlatformDomains() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<PlatformDomainDirectory | null>(null);
  const [customers, setCustomers] = useState<CustomerDetail[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");

  // A customer slug in the URL, so the count on the customers table can
  // link straight here. Kept in the URL rather than in state so the view is
  // shareable and the back button works.
  const customer = params.get("customer") ?? "";
  const region = params.get("region") ?? "";

  const load = () => {
    void get<PlatformDomainDirectory>("/api/admin/platform-domains")
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e) => setError(e.message));
    void get<{ customers: CustomerDetail[] }>("/api/admin/customers")
      .then((d) => setCustomers(d.customers))
      .catch(() => {});
  };

  useEffect(load, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { results } = await post<{ results: RefreshResult[] }>(
        "/api/admin/platform-domains/refresh",
      );
      // A partial refresh is the expected outcome, not an exception: the
      // failed regions are named and the successful ones have already
      // landed, so the table is reloaded either way.
      const failed = results.filter((r) => r.error);
      setError(
        failed.length
          ? failed.map((r) => `${r.region}: ${r.error}`).join(" · ")
          : null,
      );
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRefreshing(false);
    }
  };

  const assign = async (row: PlatformDomainRow, customerId: string) => {
    try {
      await put(`/api/admin/platform-domains/${row.region}/${row.domain_id}`, {
        customer_id: customerId || null,
      });
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.domains ?? []).filter((r) => {
      if (region && r.region !== region) return false;
      if (customer && r.customer_name !== customer) return false;
      if (filter === "unclaimed" && r.customer_id) return false;
      if (filter === "claimed" && !r.customer_id) return false;
      // The rows worth acting on: a tenant upstream has stopped listing, or
      // a link to something the mirror has never seen.
      if (filter === "issues" && r.in_directory && r.is_present) return false;
      if (!needle) return true;
      return (
        (r.identifier ?? "").toLowerCase().includes(needle) ||
        (r.name ?? "").toLowerCase().includes(needle) ||
        r.domain_id.toLowerCase().includes(needle) ||
        (r.customer_display_name ?? "").toLowerCase().includes(needle)
      );
    });
  }, [data, q, filter, region, customer]);

  const unclaimed = (data?.domains ?? []).filter((r) => !r.customer_id);
  const selected = customers.find((c) => c.name === customer);

  return (
    <>
      {error && <div className="error">{error}</div>}

      <div className="row" style={{ alignItems: "center", margin: "18px 0 6px" }}>
        <h2 style={{ margin: 0, flex: 1 }}>Platform domains</h2>
        <div className="tight">
          <button className="primary" onClick={refresh} disabled={refreshing || !data?.regions.length}>
            {refreshing ? "Refreshing…" : "Refresh now"}
          </button>
        </div>
      </div>

      <p className="sub">
        Tenants of the voice platform, which is what business metrics join on. Not the
        same thing as a customer's email domains — those are on the Customers tab and
        feed attribution instead.
      </p>

      <SyncStatus regions={data?.regions ?? []} syncs={data?.syncs ?? []} />

      <div className="row" style={{ margin: "16px 0" }}>
        <div>
          <label>Search</label>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="identifier, name, customer or id"
          />
        </div>
        <div>
          <label>Region</label>
          <select value={region} onChange={(e) => setParam("region", e.target.value)}>
            <option value="">all</option>
            <option value="global">global</option>
            <option value="india">india</option>
          </select>
        </div>
        <div>
          <label>Show</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value as Filter)}>
            <option value="all">all</option>
            <option value="unclaimed">unclaimed ({unclaimed.length})</option>
            <option value="claimed">claimed</option>
            <option value="issues">needs attention</option>
          </select>
        </div>
        <div>
          <label>Customer</label>
          <select value={customer} onChange={(e) => setParam("customer", e.target.value)}>
            <option value="">all</option>
            {customers.map((c) => (
              <option key={c.id} value={c.name}>
                {c.display_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selected && (
        <AssignAnother
          customer={selected}
          unclaimed={unclaimed}
          onAssign={(row) => assign(row, selected.id)}
        />
      )}

      {!data ? (
        <div className="empty">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="empty">
          {data.domains.length === 0
            ? "Nothing here yet — press Refresh now, or run `make sync-domains`."
            : "No domain matches those filters."}
        </div>
      ) : (
        <>
          <p className="sub">
            {rows.length} of {data.domains.length}
          </p>
          <table>
            <thead>
              <tr>
                <th>Region</th>
                <th>Identifier</th>
                <th>Name</th>
                <th style={{ width: 220 }}>Customer</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.region}:${r.domain_id}`} className={r.is_present ? "" : "off"}>
                  <td>{r.region}</td>
                  <td className="mono">{r.identifier ?? <span className="unset">unknown</span>}</td>
                  {/* Upstream sets name and identifier to the same string
                      today. Shown anyway, because that is their choice to
                      change and not ours to assume. */}
                  <td>{r.name === r.identifier ? "" : (r.name ?? "")}</td>
                  <td>
                    <select
                      value={r.customer_id ?? ""}
                      onChange={(e) => assign(r, e.target.value)}
                    >
                      <option value="">—</option>
                      {customers.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.display_name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="sub">
                    <Notes row={r} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

/** Attaching a tenant while looking at one customer.
 *
 * The table below is filtered to that customer, so it can show what they
 * already have but has nowhere to put something they do not. This is that
 * place, and it only ever offers **unclaimed** domains: moving one off
 * another customer is a decision to make while looking at both, which is
 * what the unfiltered table is for.
 */
function AssignAnother({
  customer,
  unclaimed,
  onAssign,
}: {
  customer: CustomerDetail;
  unclaimed: PlatformDomainRow[];
  onAssign: (row: PlatformDomainRow) => void;
}) {
  const [picked, setPicked] = useState("");

  const byRegion = ["global", "india"].map((region) => ({
    region,
    rows: unclaimed.filter((r) => r.region === region),
  }));

  return (
    <form
      className="row"
      style={{ margin: "16px 0" }}
      onSubmit={(e) => {
        e.preventDefault();
        const row = unclaimed.find((r) => `${r.region}:${r.domain_id}` === picked);
        if (row) {
          onAssign(row);
          setPicked("");
        }
      }}
    >
      <div>
        <label>Attach a platform domain to {customer.display_name}</label>
        <select value={picked} onChange={(e) => setPicked(e.target.value)}>
          <option value="">choose an unclaimed domain…</option>
          {byRegion.map(({ region, rows }) =>
            rows.length ? (
              <optgroup key={region} label={region}>
                {rows.map((r) => (
                  <option key={`${r.region}:${r.domain_id}`} value={`${r.region}:${r.domain_id}`}>
                    {r.identifier ?? r.domain_id}
                  </option>
                ))}
              </optgroup>
            ) : null,
          )}
        </select>
      </div>
      <div className="tight">
        <button className="primary" disabled={!picked}>
          Attach
        </button>
      </div>
    </form>
  );
}

/** Why a row might need a second look. Blank is the normal case. */
function Notes({ row }: { row: PlatformDomainRow }) {
  const notes: string[] = [];
  if (!row.in_directory) {
    notes.push("assigned, but this region has never listed it");
  } else if (!row.is_present) {
    notes.push("upstream has stopped listing it");
  }
  // Warned rather than refused: our own tenant on a customer only makes
  // that customer's numbers wrong, and there are real reasons to want our
  // traffic counted somewhere. Contrast `EMAIL_INTERNAL_DOMAINS`, where the
  // same mistake silently turns a filter into a no-op (invariant 12).
  if (row.is_internal) notes.push("our own tenant");
  if (!notes.length) return null;
  return <>⚠ {notes.join(" · ")}</>;
}

/** Per-region freshness. The failing half matters more than the working
    half, so an error is shown in full rather than as "failed". */
function SyncStatus({
  regions,
  syncs,
}: {
  regions: string[];
  syncs: PlatformDomainSync[];
}) {
  if (!regions.length) {
    return (
      <div className="banner">
        This deployment has no Redshift target configured, so the directory cannot be
        refreshed here. Assignments still work — a domain does not have to be mirrored
        to be assigned.
      </div>
    );
  }

  return (
    <div className="stats">
      {regions.map((region) => {
        const s = syncs.find((x) => x.region === region);
        if (!s?.last_run_at) {
          return (
            <span key={region} className="chip">
              {region} — never refreshed
            </span>
          );
        }
        return (
          <span key={region} className="chip">
            {region} — {s.domains_seen ?? 0} domains, synced{" "}
            {s.last_success_at ? <Time value={s.last_success_at} mode="relative" /> : "never"}
            {s.last_error ? ` · failing: ${s.last_error}` : ""}
          </span>
        );
      })}
    </div>
  );
}
